import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useCreateViewVersion } from '../../../../../api/abundanceEngine'
import { useAgentPanel } from './hooks/useAgentPanel'

const AgentPanelContext = createContext(null)

/**
 * Provides agent panel state to child components via context.
 * Wraps useAgentPanel and makes its output available to ChatPanel,
 * ChatMessages, etc. without prop drilling.
 */
export function AgentPanelProvider ({ agentId, initialThreadId, viewContext, onApplyJsx, onThreadIdChange, onVersionCreated, children }) {
  const panel = useAgentPanel(agentId, { initialThreadId, viewContext })
  const { mutateAsync: createVersion } = useCreateViewVersion()
  const [versionSaveStatus, setVersionSaveStatus] = useState(null)

  const isEditorMode = !!viewContext?.viewId

  const onSaveVersion = useCallback(async (jsxContent) => {
    if (!viewContext?.viewId) return

    try {
      setVersionSaveStatus('saving')
      const result = await createVersion({
        viewId: viewContext.viewId,
        content: jsxContent,
        description: 'Version created by AI editor'
      })
      setVersionSaveStatus('saved')
      if (result?.viewVersionId && onVersionCreated) {
        onVersionCreated(result.viewVersionId)
      }
      setTimeout(() => setVersionSaveStatus(null), 3000)
    } catch {
      setVersionSaveStatus('error')
      setTimeout(() => setVersionSaveStatus(null), 5000)
    }
  }, [viewContext?.viewId, createVersion, onVersionCreated])

  React.useEffect(() => {
    if (panel.threadId && onThreadIdChange) {
      onThreadIdChange(panel.threadId)
    }
  }, [panel.threadId, onThreadIdChange])

  const value = useMemo(() => ({
    ...panel,
    onApplyJsx,
    onSaveVersion: isEditorMode ? onSaveVersion : undefined,
    isEditorMode,
    versionSaveStatus
  }), [panel, onApplyJsx, onSaveVersion, isEditorMode, versionSaveStatus])

  return (
    <AgentPanelContext.Provider value={value}>
      {children}
    </AgentPanelContext.Provider>
  )
}

AgentPanelProvider.propTypes = {
  agentId: PropTypes.string.isRequired,
  initialThreadId: PropTypes.string,
  viewContext: PropTypes.shape({
    viewId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    content: PropTypes.string
  }),
  onApplyJsx: PropTypes.func,
  onThreadIdChange: PropTypes.func,
  onVersionCreated: PropTypes.func,
  children: PropTypes.node
}

export function useAgentPanelContext () {
  const context = useContext(AgentPanelContext)
  if (!context) {
    throw new Error('useAgentPanelContext must be used within an AgentPanelProvider')
  }
  return context
}
